import { RecurrenceType } from '@shared/modules/client-task-schedule-entries/client-task-schedule-entry';
import { DAY_LABELS } from '@shared/modules/schedule/index';
import type { ScheduleEntry } from '@shared/modules/schedule/schedule-entries';
import { Presenter } from '@shared/presenters/presenter';
import { secondsToReadable } from '@shared/utils/date-time';
import { niceList } from '@shared/utils/strings';
import { DateTime } from 'luxon';
import { RRule } from 'rrule';
import { UserPresenter } from '../users/user.presenter';

export abstract class ScheduleEntryPresenter<M extends ScheduleEntry> extends Presenter<M> {
    get isRepeated(): boolean {
        return this._get('isRepeated', () => {
            const day = this.data.day;
            const week = this.data.week;

            if (Array.isArray(day) && day.length > 1) {
                return true;
            }

            if (Array.isArray(week) && week.length > 1) {
                return true;
            }

            return false;
        });
    }

    get days(): string[] {
        return this._get('days', () => {
            const days = Array.isArray(this.data.day) ? this.data.day : [this.data.day];

            return days.map(d => DAY_LABELS[d].label);
        });
    }

    get weeks(): number[] {
        return this._get('weeks', () => {
            const weeks = Array.isArray(this.data.week) ? this.data.week : [this.data.week];

            return weeks.map(w => w + 1);
        });
    }

    get startTime(): string {
        return this._get('startTime', () => DateTime.fromFormat(this.data.start, 'HH:mm:ss').toFormat('T'));
    }

    get finishTime(): string {
        return this._get('finishTime', () => DateTime.fromFormat(this.data.start, 'HH:mm:ss')
            .plus({ seconds: this.data.duration })
            .toFormat('T'));
    }

    get duration(): number {
        return this._get('duration', () => secondsToReadable(this.get('duration')));
    }

    get readableRrule(): string {
        return this._get('readableRrule', () => {
            if (this.data.rrule) {
                const rule = RRule.fromString(this.data.rrule);

                return `${rule.toText()}, starting ${this.effectiveAppliesFrom}`;
            }
        });
    }

    get describeWhen(): string {
        return this._get('describeWhen', () => {
            switch (this.data.recurrence_type) {
                case RecurrenceType.rrule:
                    return this.readableRrule;

                case RecurrenceType.linked:
                    return this.readableRrule;

                case RecurrenceType.schedule:
                default:
                    const days = this.days
                        .map(day => day.substr(0, 2))
                        .join(', ');

                    const weeks = `W ${niceList(this.weeks)}`;

                    const description = `${days} • ${weeks}`;

                    return description;
            }
        });
    }

    get effectiveAppliesFrom(): string {
        return this._get('effectiveAppliesFrom', () => this.formatDate(this._effectiveAppliesFrom));
    }

    get effectiveAppliesUntil(): string {
        return this._get('effectiveAppliesUntil', () => this.formatDate(this._effectiveAppliesUntil));
    }

    get _effectiveAppliesFrom() {
        return this.toDateTime('applies_from');
    }

    get _effectiveAppliesUntil() {
        return this.toDateTime('applies_until');
    }

    get createdBy(): UserPresenter {
        return this.present('created_by.data', UserPresenter);
    }

    get lastEditedBy(): UserPresenter {
        return this.present('last_edited_by.data', UserPresenter);
    }
}
